import { create, tsx } from '@dojo/framework/core/vdom';
import block from '@dojo/framework/core/middleware/block';
import icache from '@dojo/framework/core/middleware/icache';
import has from '@dojo/framework/core/has';

import { BlogPreview, BlogPreviews } from '../../blocks/wp-blog-previews.block';
import getSeries from '../../blocks/wp-blog-series.block';
import getCategories from '../../blocks/wp-blog-category.block';
import { wpBaseUrl } from '../../config';
import getBlogPreviews from '../../blocks/wp-blog-previews.block';

import * as css from './BlogList.m.css';
import Text from '../../widgets/Text';
import Link from '@dojo/framework/routing/Link';
import LoadingIndicator from '../../widgets/LoadingIndicator';

const arrowLeft = require('../../assets/images/icon-solid/angle-left.svg');
const arrowRight = require('../../assets/images/icon-solid/angle-right.svg');
const openGraphUrl = require('../../assets/images/misc/open-graph.png');

interface BlogsProperties {
	termOrSlug?: never;
	type?: never;
	page: number;
}

interface SearchBlogsProperties {
	termOrSlug: string;
	type: 'category' | 'series' | 'search';
	page?: never;
}

interface LatestBlogsProperties {
	termOrSlug?: never;
	type: 'latest';
	page?: never;
}

interface WpSearchResult {
	_embedded: {
		self: [any];
	};
}

const factory = create({ block, icache }).properties<
	BlogsProperties | SearchBlogsProperties | LatestBlogsProperties
>();

const legacyImage = '2020/01/og_imageV1';

function getImageUrl(imageSource: string) {
	return imageSource.indexOf(legacyImage) !== -1 ? openGraphUrl : imageSource;
}

const getSearchResults = async (searchTerm: string = ''): Promise<BlogPreviews> => {
	const response = await fetch(
		`${wpBaseUrl}/wp-json/wp/v2/search?search=${searchTerm}&per_page=30&_embed`
	);
	const json: WpSearchResult[] = await response.json();
	const previews = json.map<BlogPreview>((result) => {
		const item = result._embedded.self[0];
		const image = {
			source_url: item.jetpack_featured_media_url
		};

		return {
			title: item.title.rendered,
			slug: item.slug,
			id: item.id,
			image,
			imageSmall: image,
			excerpt: item.excerpt.rendered.replace('[&hellip;]', ''),
			date: item.date
		};
	});
	return {
		blogPreviews: previews,
		total: previews.length,
		totalPages: 1,
		currentPage: 1,
		pageSize: previews.length
	};
};

export const BlogList = factory(function BlogList({ properties, middleware: { block, icache } }) {
	const { termOrSlug, page, type } = properties();

	const isLatestInsights = type === 'latest';
	const isSeries = type === 'series';
	const hasFeaturedBlog = page === 1;
	let title = isLatestInsights ? 'Recent Posts' : 'Recent Insights';
	let blogs: BlogPreviews | undefined | null;

	switch (type) {
		case 'series': {
			const series = block(getSeries)();
			const details = series?.find((series) => series.slug === termOrSlug);

			title = `Series: ${details?.name}`;
			blogs = block(getBlogPreviews)(100, 1, {
				series: details?.id
			});
			break;
		}
		case 'category': {
			const categories = block(getCategories)();
			const details = categories?.find((category) => category.slug === termOrSlug);

			title = `Category: ${details?.name}`;
			blogs = block(getBlogPreviews)(100, 1, {
				category: details?.id
			});
			break;
		}
		case 'search': {
			title = `Results: ${termOrSlug || ''}`;
			blogs = icache.getOrSet(termOrSlug, async () => {
				if (has('build-time-render')) {
					return undefined;
				}
				return getSearchResults(termOrSlug);
			});
			break;
		}
		case 'latest':
		default: {
			const pageSize = isLatestInsights ? 4 : page === 1 ? 10 : 9;
			blogs = block(getBlogPreviews)(pageSize, page || 1, {});
			break;
		}
	}

	if (!blogs) {
		if (type === 'search') {
			return (
				<div classes={[css.root, css.loading]}>
					<div classes={[css.pageHeaderContainer]}>
						<div classes={[css.pageHeader]}>
							{termOrSlug ? `Searching for: ${termOrSlug}` : 'Searching'}
						</div>
					</div>
					<LoadingIndicator />
				</div>
			);
		}
		return null;
	}

	const featuredBlog = blogs?.blogPreviews[0];

	return (
		<div classes={css.root}>
			<div classes={[css.pageHeaderContainer]}>
				<div classes={[css.pageHeader]}>{title}</div>
				{!isLatestInsights && !isSeries && (
					<div classes={css.searchHolder}>
						<form action="/blog/search" method="get" classes={css.searchInputWrapper}>
							<div classes={[css.searchContainer]}>
								<i title="search" classes={[css.searchIcon, 'icon-search']}></i>
								<label classes={css.searchLabel} for="term">
									Search
								</label>
								<input
									id="term"
									name="term"
									classes={css.searchInput}
									type="search"
									placeholder="Start Typing"
								/>
								<span classes={[css.searchPlaceholderLabel]}>Search</span>
							</div>
						</form>
					</div>
				)}
			</div>
			<section classes={[css.blogs]}>
				{hasFeaturedBlog && (
					<Link
						classes={[css.featured]}
						to="blogPage"
						params={{ slugOrPage: featuredBlog.slug }}
						title={featuredBlog.title}
					>
						<div classes={[css.featuredImageContainer]}>
							<img
								classes={[css.featuredImage, css.zoom]}
								src={getImageUrl(featuredBlog.image.source_url)}
								alt={featuredBlog.title}
								width={featuredBlog.image.width}
								height={featuredBlog.image.height}
							/>
						</div>
						<div classes={[css.featuredContent]}>
							<div classes={[css.featuredTitle]} innerHTML={featuredBlog.title} />
							<div classes={[css.featuredExcerptContainer]}>
								<Text type="body" as="div">
									<div
										classes={[css.featuredExcerpt]}
										innerHTML={featuredBlog.excerpt}
									/>
								</Text>
							</div>
							<div classes={[css.readMore]}>Read More</div>
						</div>
					</Link>
				)}

				{blogs.blogPreviews.map((blog, index) => {
					return (
						<Link
							classes={[css.blog, index === 0 && hasFeaturedBlog && css.firstBlog]}
							to="blogPage"
							params={{ slugOrPage: blog.slug }}
							title={blog.title}
							data-preview-slug={blog.slug}
							target="_top"
						>
							<div>
								<div classes={[css.blogImageContainer]}>
									<img
										loading="lazy"
										classes={[css.blogImage, css.zoom]}
										src={getImageUrl(blog.imageSmall.source_url)}
										height={210}
										width={397}
										alt={blog.title}
									/>
								</div>
								<div classes={[css.blogTitle]} innerHTML={blog.title} />
								<Text type="body" as="div">
									<div classes={[css.blogExcerpt]} innerHTML={blog.excerpt} />
								</Text>
								<div classes={[css.readMore]}>Read More</div>
							</div>
						</Link>
					);
				})}
			</section>
			{page && (
				<div classes={[css.paginationContainer]}>
					{page != null && page > 1 && (
						<Link
							to="blogPage"
							params={{ slugOrPage: `${page - 1}` }}
							classes={[css.prevLink]}
						>
							<div classes={[css.pagination]}>
								<img src={arrowLeft} />
								<span classes={[css.prev]}>
									<Text type="large-body">Prev</Text>
								</span>
							</div>
						</Link>
					)}
					{page != null && page < blogs.totalPages && (
						<Link
							to="blogPage"
							params={{ slugOrPage: `${(page || 1) + 1}` }}
							classes={[css.nextLink]}
							target="_top"
						>
							<div classes={[css.pagination]}>
								<span classes={[css.next]}>
									<Text type="large-body">Next</Text>
								</span>
								<img src={arrowRight} alt="next page" />
							</div>
						</Link>
					)}
				</div>
			)}
		</div>
	);
});

export default BlogList;
